import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, Search, Menu, X, ChevronDown, ChevronRight, User, MapPin } from 'lucide-react';
import nlgLogo from '../assets/images/nlg_logo.png';
import pokemonLogo from '../assets/images/pokemon_logo.png';
import mtgLogo from '../assets/images/mtg_logo.png';
import yugiohLogo from '../assets/images/yugioh_logo.png';
import riftboundLogo from '../assets/images/riftbound_logo.png';
import { useCart } from '../context/CartContext';
import { useUser } from '../context/UserContext';
import { motion, AnimatePresence } from 'framer-motion';
import UserAccountModal from './UserAccountModal';
import apiService from '../services/nextlevelgames-api';
import SearchBox from './SearchBox';
import { useStore } from '../context/StoreContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [latestSets, setLatestSets] = useState({});
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [tcgCategories, setTcgCategories] = useState([]);
  const [expandedTcg, setExpandedTcg] = useState(null);
  const [flyoutTop, setFlyoutTop] = useState(0);
  const primaryPanelRef = useRef(null);
  const morePanelRef = useRef(null);
  const [mobileTcgParent, setMobileTcgParent] = useState(null);
  const [otherCategories, setOtherCategories] = useState([]);
  const [expandedMore, setExpandedMore] = useState(null);
  const [moreFlyoutTop, setMoreFlyoutTop] = useState(0);
  const [mobileMoreParent, setMobileMoreParent] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { cartItems } = useCart();
  const { isLoggedIn, customer } = useUser();
  const { locations, selectedLocation, setSelectedLocation } = useStore();

  const handleUserClick = () => {
    if (isLoggedIn) {
      navigate('/account');
    } else {
      setIsUserModalOpen(true);
    }
  };

  const openSinglesFlyout = (key) => (e) => {
    setExpandedTcg(key);
    if (primaryPanelRef.current) {
      const btnRect = e.currentTarget.getBoundingClientRect();
      const panelRect = primaryPanelRef.current.getBoundingClientRect();
      setFlyoutTop(btnRect.top - panelRect.top);
    }
  };

  const cartItemCount = cartItems.reduce((total, item) => total + item.quantity, 0);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const MORE_SHOP_NAMES = ['Table Top Games', 'Cardboard Accessories', "Plushes & Funko POP's", 'Video Games'];
  // Categories shown under the generic "Other Products" entry's flyout. Some of these
  // (e.g. All The Things Custom Designs) are nested under another category, not a root.
  const OTHER_PRODUCTS_SUBCATEGORY_NAMES = [
    'Cardboard Accessories', 'Dice & Accessories', 'Books & Magazines',
    'Our Local Artists', 'All The Things Custom Designs',
  ];

  const findCategoryByName = (roots, name) => {
    const root = roots.find(c => c.name === name);
    if (root) return root;
    for (const r of roots) {
      const child = (r.children || []).find(c => c.name === name);
      if (child) return child;
    }
    return null;
  };

  useEffect(() => {
    apiService.getCategoryTree().then(data => {
      const roots = data.categories || [];

      const tcgRoot = roots.find(c => c.name === 'Trading Card Games');
      if (tcgRoot) {
        const PRIORITY = ['Pokémon Sealed', 'Magic The Gathering Sealed'];
        const children = tcgRoot.children || [];
        const pinned = PRIORITY.map(n => children.find(c => c.name === n)).filter(Boolean);
        const rest = children.filter(c => !PRIORITY.includes(c.name));
        setTcgCategories([...pinned, ...rest]);
      }

      const moreRoots = MORE_SHOP_NAMES.map(name => roots.find(c => c.name === name)).filter(Boolean);

      const otherProductsEntry = {
        id: '__other_products__',
        name: 'Other Products',
        isGeneric: true,
        children: OTHER_PRODUCTS_SUBCATEGORY_NAMES.map(name => findCategoryByName(roots, name)).filter(Boolean),
      };

      setOtherCategories([...moreRoots, otherProductsEntry]);

      // Newest sets per game, ordered by when the set category was last updated
      const singlesRoot = roots.find(c => c.name === 'Singles');
      const newestSets = (gameName) => {
        const game = (singlesRoot?.children || []).find(c => c.name === gameName);
        const setsGroup = (game?.children || []).find(c => /sets$/i.test(c.name));
        return (setsGroup?.children || [])
          .filter(c => c.updated_at)
          .sort((a, b) => b.updated_at.localeCompare(a.updated_at))
          .slice(0, 10);
      };
      setLatestSets({
        'singles:pokemon': newestSets('Pokemon Singles'),
        'singles:magic': newestSets('Magic the Gathering Singles'),
      });
    }).catch(() => {});
  }, []);

  const categories = [
    { name: 'Pokemon', path: '/shop/pokemon', icon: pokemonLogo, isImage: true },
    { name: 'Magic: The Gathering', path: '/shop/magic', icon: mtgLogo, isImage: true },
  ];

  return (
    <header className={`fixed w-full top-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-md'
    }`}>
      <nav className="mx-auto px-4 sm:px-6 lg:px-8 max-w-[1600px]">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group flex-shrink-0">
            <img src={nlgLogo} alt="Next Level Games" className="w-14 h-14 lg:w-16 lg:h-16 object-contain" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6 xl:space-x-8 whitespace-nowrap">
            <Link
              to="/"
              className={`font-medium transition-colors hover:text-primary-600 ${
                location.pathname === '/' ? 'text-primary-600' : 'text-gray-700'
              }`}
            >
              Home
            </Link>

            {/* Shop Dropdown */}
            <div className="relative">
              <button
                onMouseEnter={() => setDropdownOpen('shop')}
                onMouseLeave={() => setDropdownOpen(null)}
                className={`flex items-center space-x-1 font-medium transition-colors hover:text-primary-600 ${
                  location.pathname.startsWith('/shop') ? 'text-primary-600' : 'text-gray-700'
                }`}
              >
                <span>Shop</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              
              <AnimatePresence>
                {dropdownOpen === 'shop' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    onMouseEnter={() => setDropdownOpen('shop')}
                    onMouseLeave={() => { setDropdownOpen(null); setExpandedTcg(null); }}
                    className="absolute top-full left-0 mt-2 flex items-start z-50"
                  >
                    {/* Primary panel */}
                    <div ref={primaryPanelRef} className="w-72 bg-white rounded-xl shadow-2xl border border-gray-100">
                      <div className="p-2 max-h-[70vh] overflow-y-auto">
                        <p className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">Singles</p>
                        <Link
                          to="/shop/pokemon"
                          onMouseEnter={openSinglesFlyout('singles:pokemon')}
                          className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-colors group ${expandedTcg === 'singles:pokemon' ? 'bg-primary-50' : 'hover:bg-primary-50'}`}
                        >
                          <img src={pokemonLogo} alt="Pokemon" className="w-5 h-5 object-contain" />
                          <span className="text-gray-700 group-hover:text-primary-600 transition-colors flex-1">Pokemon</span>
                          <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 ml-2" />
                        </Link>
                        <Link
                          to="/shop/magic"
                          onMouseEnter={openSinglesFlyout('singles:magic')}
                          className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-colors group ${expandedTcg === 'singles:magic' ? 'bg-primary-50' : 'hover:bg-primary-50'}`}
                        >
                          <img src={mtgLogo} alt="Magic: The Gathering" className="w-5 h-5 object-contain" />
                          <span className="text-gray-700 group-hover:text-primary-600 transition-colors flex-1">Magic: The Gathering</span>
                          <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 ml-2" />
                        </Link>
                        <Link
                          to="/shop/yugioh"
                          onMouseEnter={() => setExpandedTcg(null)}
                          className="flex items-center space-x-3 px-4 py-2 rounded-lg hover:bg-primary-50 transition-colors group"
                        >
                          <img src={yugiohLogo} alt="Yu-Gi-Oh!" className="w-5 h-5 object-contain" />
                          <span className="text-gray-700 group-hover:text-primary-600 transition-colors">Yu-Gi-Oh!</span>
                        </Link>
                        <Link
                          to="/shop/onepiece"
                          onMouseEnter={() => setExpandedTcg(null)}
                          className="flex items-center space-x-3 px-4 py-2 rounded-lg hover:bg-primary-50 transition-colors group"
                        >
                          <span className="text-lg leading-none">🏴‍☠️</span>
                          <span className="text-gray-700 group-hover:text-primary-600 transition-colors">One Piece</span>
                        </Link>
                        <Link
                          to="/shop/pokemonjapan"
                          onMouseEnter={() => setExpandedTcg(null)}
                          className="flex items-center space-x-3 px-4 py-2 rounded-lg hover:bg-primary-50 transition-colors group"
                        >
                          <img src={pokemonLogo} alt="Pokemon Japan" className="w-5 h-5 object-contain" />
                          <span className="text-gray-700 group-hover:text-primary-600 transition-colors">Pokemon Japan</span>
                        </Link>
                        <Link
                          to="/shop/riftbound"
                          onMouseEnter={() => setExpandedTcg(null)}
                          className="flex items-center space-x-3 px-4 py-2 rounded-lg hover:bg-primary-50 transition-colors group"
                        >
                          <img src={riftboundLogo} alt="Riftbound" className="w-5 h-5 object-contain" />
                          <span className="text-gray-700 group-hover:text-primary-600 transition-colors">Riftbound</span>
                        </Link>
                        <Link
                          to="/shop/gradedsingles"
                          onMouseEnter={() => setExpandedTcg(null)}
                          className="flex items-center space-x-3 px-4 py-2 rounded-lg hover:bg-primary-50 transition-colors group"
                        >
                          <span className="text-lg leading-none">🎴</span>
                          <span className="text-gray-700 group-hover:text-primary-600 transition-colors">Graded Singles</span>
                        </Link>

                        {tcgCategories.length > 0 && (
                          <>
                            <div className="border-t border-gray-100 my-2" />
                            <p className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">Trading Card Games</p>
                            {tcgCategories.map(parent => (
                              <button
                                key={parent.id}
                                onMouseEnter={(e) => {
                                  setExpandedTcg(parent.id);
                                  if (primaryPanelRef.current) {
                                    const btnRect = e.currentTarget.getBoundingClientRect();
                                    const panelRect = primaryPanelRef.current.getBoundingClientRect();
                                    setFlyoutTop(btnRect.top - panelRect.top);
                                  }
                                }}
                                className={`w-full flex items-center justify-between px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${expandedTcg === parent.id ? 'bg-primary-50 text-primary-600' : 'text-gray-800 hover:bg-primary-50'}`}
                              >
                                <span className="text-left">{parent.name}</span>
                                <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 ml-2" />
                              </button>
                            ))}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Flyout panel */}
                    <AnimatePresence>
                      {expandedTcg && (() => {
                        if (latestSets[expandedTcg]) {
                          const gameKey = expandedTcg.replace('singles:', '');
                          const gameLabel = gameKey === 'magic' ? 'Magic' : 'Pokemon';
                          return (
                            <motion.div
                              key="singles-flyout"
                              initial={{ opacity: 0, x: -8 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: -8 }}
                              transition={{ duration: 0.15 }}
                              className="ml-1 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 flex-shrink-0"
                              style={{ marginTop: flyoutTop }}
                            >
                              <div className="p-2 max-h-[70vh] overflow-y-auto">
                                {latestSets[expandedTcg].map(set => (
                                  <Link
                                    key={set.id}
                                    to={`/shop/${gameKey}?set=${encodeURIComponent(set.name)}`}
                                    className="block px-4 py-1.5 text-sm text-gray-600 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-colors"
                                  >
                                    {set.name}
                                  </Link>
                                ))}
                                <div className="border-t border-gray-100 my-1" />
                                <Link
                                  to={`/shop/${gameKey}`}
                                  className="block px-4 py-2 text-sm font-semibold text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                >
                                  All {gameLabel} Singles
                                </Link>
                              </div>
                            </motion.div>
                          );
                        }
                        const activeTcg = tcgCategories.find(p => p.id === expandedTcg);
                        return activeTcg ? (
                          <motion.div
                            key="tcg-flyout"
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -8 }}
                            transition={{ duration: 0.15 }}
                            className="ml-1 w-60 bg-white rounded-xl shadow-2xl border border-gray-100 flex-shrink-0"
                            style={{ marginTop: flyoutTop }}
                          >
                            <div className="p-2 max-h-[70vh] overflow-y-auto">
                              <Link
                                to={`/shop/tradingcardgames?category_id=${activeTcg.id}`}
                                className="block px-4 py-2 text-sm font-semibold text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                              >
                                Shop All {activeTcg.name}
                              </Link>
                              {activeTcg.children.length > 0 && (
                                <>
                                  <div className="border-t border-gray-100 my-1" />
                                  {activeTcg.children.map(child => (
                                    <Link
                                      key={child.id}
                                      to={`/shop/tradingcardgames?category_id=${child.id}`}
                                      className="block px-4 py-1.5 text-sm text-gray-600 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-colors"
                                    >
                                      {child.name}
                                    </Link>
                                  ))}
                                </>
                              )}
                            </div>
                          </motion.div>
                        ) : null;
                      })()}
                    </AnimatePresence>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              to="/events?location=muncie"
              className={`font-medium transition-colors hover:text-primary-600 ${
                location.pathname === '/events' && new URLSearchParams(location.search).get('location') === 'muncie' ? 'text-primary-600' : 'text-gray-700'
              }`}
            >
              Muncie Events
            </Link>

            <Link
              to="/events?location=carmel"
              className={`font-medium transition-colors hover:text-primary-600 ${
                location.pathname === '/events' && new URLSearchParams(location.search).get('location') === 'carmel' ? 'text-primary-600' : 'text-gray-700'
              }`}
            >
              Carmel Events
            </Link>

            {/* Sell us your Cards Dropdown */}
            <div className="relative">
              <button
                onMouseEnter={() => setDropdownOpen('sell')}
                onMouseLeave={() => setDropdownOpen(null)}
                className="flex items-center space-x-1 font-medium transition-colors hover:text-primary-600 text-gray-700"
              >
                <span>Sell us your Cards</span>
                <ChevronDown className="w-4 h-4" />
              </button>

              <AnimatePresence>
                {dropdownOpen === 'sell' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    onMouseEnter={() => setDropdownOpen('sell')}
                    onMouseLeave={() => setDropdownOpen(null)}
                    className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 z-50"
                  >
                    <div className="p-2">
                      {[
                        { label: 'Muncie Buylist', href: 'https://buylist.sortswift.com/?s=nextlevelgamesmuncie' },
                        { label: 'Carmel Buylist', href: 'https://buylist.sortswift.com/?s=nextlevelgamescarmel' },
                      ].map(({ label, href }) => (
                        <a
                          key={label}
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block px-4 py-2.5 rounded-lg hover:bg-primary-50 transition-colors text-gray-700 hover:text-primary-600 font-medium text-sm"
                        >
                          {label}
                        </a>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* <Link
              to="/about"
              className={`font-medium transition-colors hover:text-primary-600 ${
                location.pathname === '/about' ? 'text-primary-600' : 'text-gray-700'
              }`}
            >
              About
            </Link> */}

            {/* More Dropdown */}
            <div className="relative">
              <button
                onMouseEnter={() => setDropdownOpen('more')}
                onMouseLeave={() => setDropdownOpen(null)}
                className="flex items-center space-x-1 font-medium transition-colors hover:text-primary-600 text-gray-700"
              >
                <span>More</span>
                <ChevronDown className="w-4 h-4" />
              </button>

              <AnimatePresence>
                {dropdownOpen === 'more' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    onMouseEnter={() => setDropdownOpen('more')}
                    onMouseLeave={() => { setDropdownOpen(null); setExpandedMore(null); }}
                    className="absolute top-full left-0 mt-2 flex items-start z-50"
                  >
                    {/* Primary panel */}
                    <div ref={morePanelRef} className="w-72 bg-white rounded-xl shadow-2xl border border-gray-100">
                      <div className="p-2 max-h-[70vh] overflow-y-auto">
                        {otherCategories.length > 0 && (
                          <>
                            <p className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">Shop</p>
                            {otherCategories.map(parent => (
                              <button
                                key={parent.id}
                                onMouseEnter={(e) => {
                                  setExpandedMore(parent.id);
                                  if (morePanelRef.current) {
                                    const btnRect = e.currentTarget.getBoundingClientRect();
                                    const panelRect = morePanelRef.current.getBoundingClientRect();
                                    setMoreFlyoutTop(btnRect.top - panelRect.top);
                                  }
                                }}
                                onClick={() => { navigate(parent.isGeneric ? '/shop/other' : `/shop/other?category_id=${parent.id}`); setDropdownOpen(null); setExpandedMore(null); }}
                                className={`w-full flex items-center justify-between px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${expandedMore === parent.id ? 'bg-primary-50 text-primary-600' : 'text-gray-800 hover:bg-primary-50'}`}
                              >
                                <span className="text-left">{parent.name}</span>
                                <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 ml-2" />
                              </button>
                            ))}
                            <div className="border-t border-gray-100 my-2" />
                          </>
                        )}

                        {[
                          { label: 'Trade Policy', href: '/trade-policy' },
                          { label: 'Pre Owned Video Game Return Policy', href: '/return-policy' },
                          { label: 'Video Game Trade Policy', href: '/video-game-trade-policy' },
                        ].map(({ label, href }) => (
                          <a
                            key={label}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block px-4 py-2.5 rounded-lg hover:bg-primary-50 transition-colors text-gray-700 hover:text-primary-600 font-medium text-sm"
                          >
                            {label}
                          </a>
                        ))}
                      </div>
                    </div>

                    {/* Flyout panel for More shop categories */}
                    <AnimatePresence>
                      {expandedMore && (() => {
                        const activeMore = otherCategories.find(p => p.id === expandedMore);
                        return activeMore ? (
                          <motion.div
                            key="more-flyout"
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -8 }}
                            transition={{ duration: 0.15 }}
                            className="ml-1 w-60 bg-white rounded-xl shadow-2xl border border-gray-100 flex-shrink-0"
                            style={{ marginTop: moreFlyoutTop }}
                          >
                            <div className="p-2 max-h-[70vh] overflow-y-auto">
                              <Link
                                to={activeMore.isGeneric ? '/shop/other' : `/shop/other?category_id=${activeMore.id}`}
                                onClick={() => { setDropdownOpen(null); setExpandedMore(null); }}
                                className="block px-4 py-2 text-sm font-semibold text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                              >
                                Shop All {activeMore.name}
                              </Link>
                              {(activeMore.children || []).length > 0 && (
                                <>
                                  <div className="border-t border-gray-100 my-1" />
                                  {activeMore.children.map(child => (
                                    <Link
                                      key={child.id}
                                      to={`/shop/other?category_id=${child.id}`}
                                      onClick={() => { setDropdownOpen(null); setExpandedMore(null); }}
                                      className="block px-4 py-1.5 text-sm text-gray-600 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-colors"
                                    >
                                      {child.name}
                                    </Link>
                                  ))}
                                </>
                              )}
                            </div>
                          </motion.div>
                        ) : null;
                      })()}
                    </AnimatePresence>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1 justify-end lg:flex-none">
            {/* Search: inline bar on phones and wide screens, icon in between */}
            <div className="flex-1 min-w-0 pl-3 lg:hidden xl:block xl:flex-none xl:pl-0 xl:w-56 2xl:w-72">
              <SearchBox compact placeholder="Search Next Level Games" />
            </div>
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors hidden lg:block xl:hidden"
              aria-label="Search"
            >
              <Search className="w-5 h-5 text-gray-700" />
            </button>

            {/* User Account */}
            <button 
              onClick={handleUserClick}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors hidden sm:flex items-center gap-2"
            >
              {isLoggedIn && customer?.given_name ? (
                <>
                  <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-sm font-semibold text-primary-600">
                      {customer.given_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-gray-700">{customer.given_name}</span>
                </>
              ) : (
                <User className="w-5 h-5 text-gray-700" />
              )}
            </button>

            {/* Store Picker */}
            {locations.length > 0 && (
              <div className="relative hidden lg:block">
                <button
                  onMouseEnter={() => setDropdownOpen('store')}
                  onMouseLeave={() => setDropdownOpen(null)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 hover:border-primary-400 hover:bg-primary-50 transition-colors text-sm"
                >
                  <MapPin className="w-3.5 h-3.5 text-primary-600 flex-shrink-0" />
                  <span className="text-gray-700 max-w-[120px] truncate">
                    {selectedLocation ? selectedLocation.name : 'Select a store'}
                  </span>
                  <ChevronDown className="w-3 h-3 text-gray-400 flex-shrink-0" />
                </button>
                <AnimatePresence>
                  {dropdownOpen === 'store' && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      onMouseEnter={() => setDropdownOpen('store')}
                      onMouseLeave={() => setDropdownOpen(null)}
                      className="absolute top-full right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 p-3 z-50"
                    >
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">Select your store</p>
                      {locations.map(loc => (
                        <button
                          key={loc.id}
                          onClick={() => { setSelectedLocation(selectedLocation?.id === loc.id ? null : loc); setDropdownOpen(null); }}
                          className={`w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-lg transition-colors mb-1 ${selectedLocation?.id === loc.id ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-50 text-gray-700'}`}
                        >
                          <MapPin className={`w-4 h-4 mt-0.5 flex-shrink-0 ${selectedLocation?.id === loc.id ? 'text-primary-600' : 'text-gray-400'}`} />
                          <div>
                            <div className="font-medium text-sm">{loc.name}</div>
                            <div className="text-xs text-gray-500">{loc.address?.address_line1}, {loc.address?.locality}, {loc.address?.administrative_district_level1}</div>
                          </div>
                          {selectedLocation?.id === loc.id && (
                            <div className="ml-auto w-2 h-2 rounded-full bg-primary-600 mt-1.5 flex-shrink-0" />
                          )}
                        </button>
                      ))}
                      {selectedLocation && (
                        <button
                          onClick={() => { setSelectedLocation(null); setDropdownOpen(null); }}
                          className="w-full text-center text-xs text-gray-400 hover:text-gray-600 mt-1 py-1"
                        >
                          Clear selection
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Cart */}
            <Link to="/cart" className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors">
              <ShoppingCart className="w-5 h-5 text-gray-700" />
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <AnimatePresence>
          {isSearchOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="hidden lg:block xl:hidden"
            >
              <div className="py-4">
                <SearchBox autoFocus onNavigate={() => setIsSearchOpen(false)} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="lg:hidden overflow-y-auto overscroll-contain max-h-[calc(100dvh-4rem)] border-t border-gray-100"
            >
              <div className="py-4 space-y-1">
                <Link
                  to="/"
                  className="block px-4 py-2 text-gray-700 hover:bg-primary-50 rounded-lg transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Home
                </Link>

                {/* Shop accordion */}
                <div>
                  <button
                    onClick={() => { setDropdownOpen(prev => prev === 'mobile-shop' ? null : 'mobile-shop'); setMobileTcgParent(null); }}
                    className="w-full flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    <span>Shop</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${dropdownOpen === 'mobile-shop' ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {dropdownOpen === 'mobile-shop' && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <AnimatePresence mode="wait">
                          {mobileTcgParent ? (
                            /* Drill-down: child view */
                            <motion.div
                              key="tcg-children"
                              initial={{ opacity: 0, x: 20 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: 20 }}
                              transition={{ duration: 0.18 }}
                              className="pl-4 space-y-1 mt-1"
                            >
                              <button
                                onClick={() => setMobileTcgParent(null)}
                                className="flex items-center gap-1 px-4 py-2 text-sm text-gray-500 hover:text-primary-600 transition-colors"
                              >
                                <ChevronRight className="w-3.5 h-3.5 rotate-180" />
                                Back
                              </button>
                              <p className="px-4 pt-1 pb-0.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">{mobileTcgParent.name}</p>
                              <Link
                                to={`/shop/tradingcardgames?category_id=${mobileTcgParent.id}`}
                                className="block px-4 py-2 text-sm font-semibold text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                onClick={() => { setIsMenuOpen(false); setMobileTcgParent(null); }}
                              >
                                Shop All {mobileTcgParent.name}
                              </Link>
                              {mobileTcgParent.children.map(child => (
                                <Link
                                  key={child.id}
                                  to={`/shop/tradingcardgames?category_id=${child.id}`}
                                  className="block px-4 py-1.5 text-sm text-gray-600 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-colors"
                                  onClick={() => { setIsMenuOpen(false); setMobileTcgParent(null); }}
                                >
                                  {child.name}
                                </Link>
                              ))}
                            </motion.div>
                          ) : (
                            /* Top-level shop view */
                            <motion.div
                              key="shop-top"
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: -20 }}
                              transition={{ duration: 0.18 }}
                              className="pl-4 space-y-1 mt-1"
                            >
                              <p className="px-4 pt-1 pb-0.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">Singles</p>
                              <Link to="/shop/pokemon" className="flex items-center space-x-3 px-4 py-2 text-gray-600 hover:bg-primary-50 rounded-lg transition-colors" onClick={() => setIsMenuOpen(false)}>
                                <img src={pokemonLogo} alt="Pokemon" className="w-5 h-5 object-contain" />
                                <span>Pokemon</span>
                              </Link>
                              <Link to="/shop/magic" className="flex items-center space-x-3 px-4 py-2 text-gray-600 hover:bg-primary-50 rounded-lg transition-colors" onClick={() => setIsMenuOpen(false)}>
                                <img src={mtgLogo} alt="Magic: The Gathering" className="w-5 h-5 object-contain" />
                                <span>Magic: The Gathering</span>
                              </Link>
                              <Link to="/shop/yugioh" className="flex items-center space-x-3 px-4 py-2 text-gray-600 hover:bg-primary-50 rounded-lg transition-colors" onClick={() => setIsMenuOpen(false)}>
                                <img src={yugiohLogo} alt="Yu-Gi-Oh!" className="w-5 h-5 object-contain" />
                                <span>Yu-Gi-Oh!</span>
                              </Link>
                              <Link to="/shop/onepiece" className="flex items-center space-x-3 px-4 py-2 text-gray-600 hover:bg-primary-50 rounded-lg transition-colors" onClick={() => setIsMenuOpen(false)}>
                                <span className="text-lg leading-none">🏴‍☠️</span>
                                <span>One Piece</span>
                              </Link>
                              <Link to="/shop/pokemonjapan" className="flex items-center space-x-3 px-4 py-2 text-gray-600 hover:bg-primary-50 rounded-lg transition-colors" onClick={() => setIsMenuOpen(false)}>
                                <img src={pokemonLogo} alt="Pokemon Japan" className="w-5 h-5 object-contain" />
                                <span>Pokemon Japan</span>
                              </Link>
                              <Link to="/shop/riftbound" className="flex items-center space-x-3 px-4 py-2 text-gray-600 hover:bg-primary-50 rounded-lg transition-colors" onClick={() => setIsMenuOpen(false)}>
                                <img src={riftboundLogo} alt="Riftbound" className="w-5 h-5 object-contain" />
                                <span>Riftbound</span>
                              </Link>
                              <Link to="/shop/gradedsingles" className="flex items-center space-x-3 px-4 py-2 text-gray-600 hover:bg-primary-50 rounded-lg transition-colors" onClick={() => setIsMenuOpen(false)}>
                                <span className="text-lg leading-none">🎴</span>
                                <span>Graded Singles</span>
                              </Link>
                              {tcgCategories.length > 0 && (
                                <>
                                  <p className="px-4 pt-2 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">Trading Card Games</p>
                                  {tcgCategories.slice(0, 3).map(parent => (
                                    <button
                                      key={parent.id}
                                      onClick={() => setMobileTcgParent(parent)}
                                      className="w-full flex items-center justify-between px-4 py-1.5 text-sm font-semibold text-gray-700 hover:bg-primary-50 rounded-lg transition-colors"
                                    >
                                      <span className="text-left">{parent.name}</span>
                                      <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                    </button>
                                  ))}
                                  {tcgCategories.length > 3 && (
                                    <Link
                                      to="/shop/tradingcardgames"
                                      className="block px-4 py-1.5 text-sm font-semibold text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                      onClick={() => setIsMenuOpen(false)}
                                    >
                                      See All Trading Card Games →
                                    </Link>
                                  )}
                                </>
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <Link
                  to="/events?location=muncie"
                  className="block px-4 py-2 text-gray-700 hover:bg-primary-50 rounded-lg transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Muncie Events
                </Link>
                <Link
                  to="/events?location=carmel"
                  className="block px-4 py-2 text-gray-700 hover:bg-primary-50 rounded-lg transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Carmel Events
                </Link>

                {/* Sell us your Cards accordion */}
                <div>
                  <button
                    onClick={() => setDropdownOpen(prev => prev === 'mobile-sell' ? null : 'mobile-sell')}
                    className="w-full flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    <span>Sell us your Cards</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${dropdownOpen === 'mobile-sell' ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {dropdownOpen === 'mobile-sell' && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="pl-4 space-y-1 mt-1">
                          {[
                            { label: 'Muncie Buylist', href: 'https://buylist.sortswift.com/?s=nextlevelgamesmuncie' },
                            { label: 'Carmel Buylist', href: 'https://buylist.sortswift.com/?s=nextlevelgamescarmel' },
                          ].map(({ label, href }) => (
                            <a
                              key={label}
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block px-4 py-2 text-gray-600 hover:bg-primary-50 rounded-lg transition-colors text-sm"
                              onClick={() => setIsMenuOpen(false)}
                            >
                              {label}
                            </a>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* <Link
                  to="/about"
                  className="block px-4 py-2 text-gray-700 hover:bg-primary-50 rounded-lg transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  About
                </Link> */}

                {/* More accordion */}
                <div>
                  <button
                    onClick={() => { setDropdownOpen(prev => prev === 'mobile-more' ? null : 'mobile-more'); setMobileMoreParent(null); }}
                    className="w-full flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    <span>More</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${dropdownOpen === 'mobile-more' ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {dropdownOpen === 'mobile-more' && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <AnimatePresence mode="wait">
                          {mobileMoreParent ? (
                            <motion.div
                              key="more-children"
                              initial={{ opacity: 0, x: 20 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: 20 }}
                              transition={{ duration: 0.18 }}
                              className="pl-4 space-y-1 mt-1"
                            >
                              <button
                                onClick={() => setMobileMoreParent(null)}
                                className="flex items-center gap-1 px-4 py-2 text-sm text-gray-500 hover:text-primary-600 transition-colors"
                              >
                                <ChevronRight className="w-3.5 h-3.5 rotate-180" />
                                Back
                              </button>
                              <p className="px-4 pt-1 pb-0.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">{mobileMoreParent.name}</p>
                              <Link
                                to={mobileMoreParent.isGeneric ? '/shop/other' : `/shop/other?category_id=${mobileMoreParent.id}`}
                                className="block px-4 py-2 text-sm font-semibold text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                onClick={() => { setIsMenuOpen(false); setMobileMoreParent(null); }}
                              >
                                Shop All {mobileMoreParent.name}
                              </Link>
                              {(mobileMoreParent.children || []).map(child => (
                                <Link
                                  key={child.id}
                                  to={`/shop/other?category_id=${child.id}`}
                                  className="block px-4 py-1.5 text-sm text-gray-600 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-colors"
                                  onClick={() => { setIsMenuOpen(false); setMobileMoreParent(null); }}
                                >
                                  {child.name}
                                </Link>
                              ))}
                            </motion.div>
                          ) : (
                            <motion.div
                              key="more-top"
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: -20 }}
                              transition={{ duration: 0.18 }}
                              className="pl-4 space-y-1 mt-1"
                            >
                              {otherCategories.length > 0 && (
                                <>
                                  <p className="px-4 pt-1 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">Shop</p>
                                  {otherCategories.map(parent => (
                                    <button
                                      key={parent.id}
                                      onClick={() => setMobileMoreParent(parent)}
                                      className="w-full flex items-center justify-between px-4 py-1.5 text-sm font-semibold text-gray-700 hover:bg-primary-50 rounded-lg transition-colors"
                                    >
                                      <span className="text-left">{parent.name}</span>
                                      <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                    </button>
                                  ))}
                                </>
                              )}
                              <div className="border-t border-gray-100 my-1" />
                              {[
                                { label: 'Trade Policy', href: '/trade-policy' },
                                { label: 'Pre Owned Video Game Return Policy', href: '/return-policy' },
                                { label: 'Video Game Trade Policy', href: '/video-game-trade-policy' },
                              ].map(({ label, href }) => (
                                <a
                                  key={label}
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block px-4 py-2 text-gray-600 hover:bg-primary-50 rounded-lg transition-colors text-sm"
                                  onClick={() => setIsMenuOpen(false)}
                                >
                                  {label}
                                </a>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Mobile store picker */}
                {locations.length > 0 && (
                  <div className="mx-4 mt-2 pt-3 border-t border-gray-100">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Your store</p>
                    <div className="space-y-1">
                      {locations.map(loc => (
                        <button
                          key={loc.id}
                          onClick={() => { setSelectedLocation(selectedLocation?.id === loc.id ? null : loc); setIsMenuOpen(false); }}
                          className={`w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-lg transition-colors ${selectedLocation?.id === loc.id ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-50 text-gray-700'}`}
                        >
                          <MapPin className={`w-4 h-4 mt-0.5 flex-shrink-0 ${selectedLocation?.id === loc.id ? 'text-primary-600' : 'text-gray-400'}`} />
                          <div>
                            <div className="font-medium text-sm">{loc.name}</div>
                            <div className="text-xs text-gray-500">{loc.address?.address_line1}, {loc.address?.locality}, {loc.address?.administrative_district_level1}</div>
                          </div>
                          {selectedLocation?.id === loc.id && (
                            <div className="ml-auto w-2 h-2 rounded-full bg-primary-600 mt-1.5 flex-shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* User Account Modal */}
      <UserAccountModal 
        isOpen={isUserModalOpen} 
        onClose={() => setIsUserModalOpen(false)} 
      />
    </header>
  );
};

export default Header;
